/**
 * Hover inspection.
 *
 * Two sources of truth answer a hover, and they are queried in priority order:
 *
 *  1. **deck.gl picking** — incidents and buildings. Anything the data layers
 *     drew knows what it is.
 *  2. **MapLibre feature query** — streets and basemap features underneath.
 *     Only consulted when deck.gl picked nothing, because a road label under
 *     an incident is not what you were pointing at.
 *
 * Everything shown is a fact from the record, never an inference.
 */

import { hovered, onClickPick, onHoverPick } from "../map/picking.js";
import { centroidOf } from "../layers/building-edits.js";
import { describeInferredDoor } from "../layers/entrances.js";
import { accessLabel, describeFloors, useLabel } from "../map/places.js";

const el = (tag, className, text) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

/**
 * A building, described the way a person would: what it is called and what it
 * is, before how tall it is.
 *
 * Footprint area, floor area and the "mid-rise / high-rise" class were dropped
 * on request — they were derived numbers dressed up as facts. Everything here
 * is either surveyed (height) or tagged by a human (the rest).
 */
let lastBuilding = null;

function describeBuilding(object, coordinate, places, buildingEdits, policeAt) {
  lastBuilding = { object, coordinate };
  const metres = object?.height ?? 0;
  const feet = Math.round(metres / 0.3048);
  const place = coordinate ? places?.nearest(coordinate) : null;
  const floors = describeFloors(metres, place);
  const edit = buildingEdits?.find(centroidOf(object?.polygon))?.fields ?? null;
  const noted = buildingEdits?.find(centroidOf(object?.polygon)) ?? null;

  const rows = [];
  const use = useLabel(place?.use);
  // A correction wins over the survey where one exists, and says so. The
  // survey value is not discarded — it is shown underneath, because "the city
  // thinks X, someone who went and looked says Y" is two facts.
  if (edit?.use) {
    rows.push(["Type", `${edit.use} (edited)`]);
    if (use && use !== edit.use) rows.push(["Type (survey)", use]);
  } else if (use) {
    rows.push(["Type", use]);
  }
  if (place?.amenity) rows.push(["Use", String(place.amenity).replace(/_/g, " ")]);
  if (place?.operator) rows.push(["Operator", place.operator]);
  if (place?.addr) rows.push(["Address", place.addr]);

  if (edit?.floors) {
    rows.push(["Floors", `${edit.floors} (edited)`]);
    if (floors.value) rows.push(["Floors (est.)", floors.value]);
  } else {
    rows.push(["Floors", floors.value]);
  }
  rows.push(["Height", `${metres.toFixed(1)} m · ${feet} ft`]);

  if (edit?.status) rows.push(["Status", `${edit.status} (edited)`]);
  if (edit?.hours) rows.push(["Hours", `${edit.hours} (edited)`]);
  else if (place?.hours) rows.push(["Hours", place.hours]);

  const access = accessLabel(place?.access);
  if (edit?.access) rows.push(["Access", `${edit.access} (edited)`]);
  else if (access) rows.push(["Access", access]);
  if (place?.phone) rows.push(["Phone", place.phone]);

  if (noted?.detail) rows.push(["Note", noted.detail]);
  const files = noted?.attachments ?? [];
  if (files.length) {
    rows.push(["Files", files.map((f) => `${f.filename} (${f.kind})`).join(", ")]);
  }
  if (edit?.tint) rows.push(["Highlighted", `${edit.tint} (edited)`]);
  if (noted?.author) rows.push(["Edited by", noted.author]);

  rows.push(["Source", place ? "Denver survey + OpenStreetMap" : "Denver Open Data · surveyed outlines"]);
  rows.push(["", window.matchMedia("(pointer: coarse)").matches
    ? "tap here to edit"
    : "click to edit"]);

  // A building tinted red is tinted because a police station sits on it, and
  // until now the tint was the only thing that said so — you could hover a red
  // building and be told it was an office. The station's own record is the
  // answer, and it is already loaded.
  const station = coordinate ? policeAt?.(coordinate) : null;
  if (station) {
    rows.unshift(["⚠ Warning", "Police building"]);
    if (station.operator) rows.push(["Operated by", station.operator]);
    if (station.phone) rows.push(["Police phone", station.phone]);
  }

  return {
    title: station ? (station.name || "Police building")
      : (edit?.name || place?.name || use || "Building"),
    rows,
  };
}

/** Pull something useful out of a basemap vector feature. */
function describeMapFeature(feature) {
  const props = feature.properties ?? {};
  const name = props.name || props["name:en"];
  if (!name) return null;

  const rows = [];
  if (props.kind) rows.push(["Kind", String(props.kind).replace(/_/g, " ")]);
  if (props.kind_detail) rows.push(["Detail", String(props.kind_detail).replace(/_/g, " ")]);
  if (props.ref) rows.push(["Route", props.ref]);
  rows.push(["Source", "OpenStreetMap via Protomaps"]);

  return { title: name, rows };
}

export function mountTooltip({
  map, overlay, layers, marks, places, buildingEdits, you, policeAt,
}) {
  const node = document.getElementById("tip");
  if (!node) return { destroy() {} };

  function hide() {
    node.hidden = true;
  }

  function show(content, x, y) {
    node.innerHTML = "";
    node.append(el("span", "tip-title", content.title));

    // Live pictures, loaded straight from the source rather than proxied.
    //
    // Cache-busted hard. CDOT serves these with an etag and a last-modified
    // but no Cache-Control, which leaves the browser free to apply heuristic
    // caching — typically a tenth of the file's age — and show a frame from
    // whenever you last hovered. A per-hover token defeats that outright.
    for (const image of content.images ?? []) {
      const figure = el("div", "tip-shot");
      const img = document.createElement("img");
      const bust = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
      const join = image.url.includes("?") ? "&" : "?";
      img.src = `${image.url}${join}_=${bust}`;
      img.alt = image.caption ?? "";
      // Eager, not lazy: the tooltip is already open and the picture is the
      // reason it was opened.
      img.loading = "eager";
      img.decoding = "async";
      figure.append(img, el("span", "tip-shot-cap", image.caption ?? ""));
      node.append(figure);
    }
    for (const [key, value] of content.rows) {
      if (value === undefined || value === null || value === "") continue;
      const row = el("div", "tip-row");
      row.append(el("span", "tip-key", key), el("span", "tip-val", String(value)));
      node.append(row);
    }
    node.hidden = false;

    // Keep the tip inside the viewport; flip it rather than let it clip.
    const box = node.getBoundingClientRect();
    const pad = 14;
    const left = x + pad + box.width > window.innerWidth ? x - box.width - pad : x + pad;
    const top = y + pad + box.height > window.innerHeight ? y - box.height - pad : y + pad;
    node.style.left = `${Math.max(4, left)}px`;
    node.style.top = `${Math.max(4, top)}px`;
  }

  /**
   * Describe whatever is under a point, given what deck already picked there.
   *
   * The pick is passed in rather than taken: see map/picking.js for why
   * nothing here may ask deck to pick for itself.
   */
  // What tapping the tip itself should do, given what the tip is showing.
  // Tracked per resolve rather than per describer: the old check read the
  // last *building* described, so a tip showing an incident could still open
  // the editor for a building tapped minutes ago.
  let tipTarget = null;

  function resolve(event, picked) {
    const { x, y } = event.point;
    tipTarget = null;

    // 1. deck.gl objects first.
    if (picked?.object) {
      const id = picked.layer?.id ?? "";
      if (picked.object.source === "feed") tipTarget = { kind: "feed", feed: picked.object };
      else if (id.startsWith("buildings")) tipTarget = { kind: "building" };
      if (id.startsWith("marks") && marks?.describe) return marks.describe(picked.object);
      // Permits share the streetwork layer but are a different record.
      if (id === "streetwork-permits") {
        const sw = layers.find((l) => l.key === "streetwork");
        if (sw?.describePermit) return sw.describePermit(picked.object);
      }
      for (const layer of layers) {
        if (id.startsWith(layer.key) && layer.describe) return layer.describe(picked.object);
      }
      if (id.startsWith("you-")) return you?.describe?.() ?? null;
      if (id === "entrances-inferred") return describeInferredDoor();
      if (id.startsWith("buildings")) {
        return describeBuilding(
          picked.object, picked.coordinate, places, buildingEdits, policeAt,
        );
      }
    }

    // 2. Basemap features underneath.
    const features = map.queryRenderedFeatures([x, y]);
    for (const feature of features) {
      const described = describeMapFeature(feature);
      if (described) return described;
    }
    return null;
  }

  // Where the cursor last was, and what deck object the tip last rendered.
  // The pick behind a hover lands on the NEXT animation frame (see
  // map/picking.js), so the render done here on the final mousemove can be one
  // object behind — and a cursor that stops moving never corrects itself. The
  // subscription below re-renders when the real answer arrives; `shownFor`
  // keeps that from re-rendering (and re-loading camera snapshots) every frame
  // the answer is unchanged.
  let lastMove = null;
  let shownFor = null;

  function onMove(event) {
    // A tooltip that follows the cursor while dragging is just noise.
    if (event.originalEvent?.buttons) { lastMove = null; return hide(); }
    lastMove = event.point;
    const picked = hovered();
    shownFor = picked?.object ?? null;
    const content = resolve(event, picked);
    if (!content) return hide();
    show(content, event.point.x, event.point.y);
  }

  // Clicking a mark opens its editor. Picking lives here, so the click has to
  // be resolved here and handed to the HUD as an event.
  /**
   * What a tap means depends on the device.
   *
   * With a mouse you hover to read and click to edit, and the two never
   * collide. A touchscreen has no hover, so a tap was doing both: you could
   * not look at a building without being thrown into its editor. On touch, a
   * tap now shows the information and a long press opens the editor — the
   * usual phone idiom for "act on this" rather than "look at this".
   */
  const TOUCH = window.matchMedia("(pointer: coarse)").matches;

  /**
   * On a phone the tip is the edit button.
   *
   * Editing a building was a long press on the map, which is undiscoverable —
   * the tip said "click to edit" on a device with nothing to click, and the one
   * gesture that worked was the one nobody thinks to try. The tip is already on
   * screen, already naming the building, and already says what it does. Tapping
   * it opens the editor.
   *
   * The long press stays: it is faster once you know it, and it works when the
   * tip is not showing.
   */
  if (TOUCH) {
    node.style.pointerEvents = "auto";
    node.addEventListener("click", (event) => {
      if (!tipTarget) return;
      event.stopPropagation();
      // A feed camera's tip is the way to its live stream: the long press
      // opens the player too, but nothing on a phone says so — the tip does.
      if (tipTarget.kind === "feed") {
        window.dispatchEvent(new CustomEvent("pulse:watch-feed", { detail: tipTarget.feed }));
        hide();
        return;
      }
      if (!lastBuilding?.object) return;
      window.dispatchEvent(new CustomEvent("pulse:edit-building", {
        detail: {
          centroid: centroidOf(lastBuilding.object?.polygon),
          survey: `Denver survey: ${(lastBuilding.object?.height ?? 0).toFixed(1)} m tall`,
        },
      }));
      hide();
    });
  }

  const LONG_PRESS_MS = 500;

  function openFor(picked) {
    if (!picked?.object) return false;
    const id = picked.layer?.id ?? "";

    // A live feed opens its player. Keyed on the row, not the layer id, so it
    // holds whether the camera came from CDOT or the city file.
    if (picked.object.source === "feed") {
      window.dispatchEvent(new CustomEvent("pulse:watch-feed", { detail: picked.object }));
      return true;
    }

    if (id.startsWith("marks")) {
      window.dispatchEvent(new CustomEvent("pulse:edit-mark", { detail: picked.object }));
      return true;
    }
    // The ghost copy is not pickable, so this cannot fire for it.
    if (id === "buildings") {
      const metres = picked.object?.height ?? 0;
      window.dispatchEvent(new CustomEvent("pulse:edit-building", {
        detail: {
          centroid: centroidOf(picked.object?.polygon),
          survey: `Denver survey: ${metres.toFixed(1)} m tall`
                + `${picked.object?.type ? ` · ${picked.object.type}` : ""}`,
        },
      }));
      return true;
    }
    return false;
  }

  if (!TOUCH) {
    // Subscribed to the pick, not to MapLibre's click: deck records the pick
    // in a microtask AFTER MapLibre's click listeners have all run, so a
    // listener here reading `clicked()` got the PREVIOUS click — the wrong
    // camera opened, every first time. See map/picking.js.
    onClickPick((picked) => openFor(picked));

    // Same latency, one frame wide, for hover: re-render the tip when the
    // pick actually lands, if it names a different object than the one the
    // last mousemove could see.
    onHoverPick((picked) => {
      if (!lastMove) return;
      if ((picked?.object ?? null) === shownFor) return;
      shownFor = picked?.object ?? null;
      const content = resolve({ point: lastMove }, picked);
      if (!content) return hide();
      show(content, lastMove.x, lastMove.y);
    });
  } else {
    let timer = null;
    let moved = false;
    let start = null;
    // A tap noted on touchend, answered when the pick arrives. The pick for
    // this tap has not been recorded yet when touchend fires — deck dispatches
    // it in a microtask after MapLibre's synthesized click, which is later
    // still — so reading `clicked()` here showed the PREVIOUS tap's tooltip:
    // tap a camera then an incident, get the camera again.
    let pendingTap = null;

    const cancel = () => {
      if (timer !== null) clearTimeout(timer);
      timer = null;
    };

    map.on("touchstart", (event) => {
      // Two fingers is a map gesture, never an edit.
      if ((event.originalEvent?.touches?.length ?? 1) > 1) return cancel();
      moved = false;
      start = event.point;
      // A new finger down abandons any tap still waiting for its pick.
      pendingTap = null;
      // The one place that still picks by hand. A press has no hover before
      // it and fires no click until it ends, so there is nothing recorded to
      // read — and the haptic at 500ms has to know by then whether the finger
      // landed on anything. This is once per gesture, not once per frame,
      // which is the distinction that matters.
      const picked = overlay.pickObject?.({
        x: event.point.x, y: event.point.y, radius: 8,
      });
      timer = setTimeout(() => {
        timer = null;
        if (moved) return;
        // Confirm the press landed on something before taking over the tap.
        if (openFor(picked) && navigator.vibrate) navigator.vibrate(12);
      }, LONG_PRESS_MS);
    });

    map.on("touchmove", (event) => {
      // A few pixels of finger drift is still a press, not a pan.
      if (start && event.point) {
        const drift = Math.hypot(event.point.x - start.x, event.point.y - start.y);
        if (drift > 12) { moved = true; cancel(); }
      } else {
        moved = true;
        cancel();
      }
    });

    map.on("touchend", (event) => {
      const wasLongPress = timer === null && !moved;
      cancel();
      if (wasLongPress || moved) { pendingTap = null; return; }
      // A short tap reads the map rather than editing it.
      pendingTap = { point: event.point, at: Date.now() };
    });

    onClickPick((picked) => {
      if (!pendingTap) return;
      const { point, at } = pendingTap;
      pendingTap = null;
      // A click that arrives long after the tap belongs to some other gesture.
      if (Date.now() - at > 1000) return;
      const content = resolve({ point }, picked);
      if (content) show(content, point.x, point.y);
      else hide();
    });

    map.on("touchcancel", cancel);
  }

  // Forgetting the cursor as well as hiding: a hover pick can land after the
  // mouse has left the map, and must not resurrect the tip.
  function hideAndForget() {
    lastMove = null;
    hide();
  }

  map.on("mousemove", onMove);
  map.on("mouseout", hideAndForget);
  map.on("dragstart", hideAndForget);
  map.on("zoomstart", hideAndForget);

  return {
    destroy() {
      map.off("mousemove", onMove);
      map.off("mouseout", hideAndForget);
    },
  };
}
